import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import Form from "@dashboard/components/Form";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import { SubmitPromise } from "@dashboard/hooks/useForm";
import useNavigator from "@dashboard/hooks/useNavigator";
import { Box } from "@saleor/macaw-ui-next";
import React from "react";

import { ProcessedService, ThirdPartyServicesFormData } from "../types";
import { ServiceCard } from "./ServiceCard";

export interface AppDetailPageProps {
  disabled: boolean;
  errors: string[];
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: ThirdPartyServicesFormData) => SubmitPromise;
  initialData?: ProcessedService[];
  appType: string; // 'google' | 'dealer-locator' | etc
}

export const AppDetailPage: React.FC<AppDetailPageProps> = ({
  disabled,
  errors,
  saveButtonBarState,
  onSubmit,
  initialData = [],
  appType,
}) => {
  const navigate = useNavigator();

  // Filter services based on app type
  const getFilteredServices = () => {
    if (appType === 'google') {
      return initialData.filter(service => service.type === 'external');
    } else if (appType === 'dealer-locator') {
      return initialData.filter(service => service.app_name === 'dealer_locator');
    } else {
      // For other internal apps, filter by app name
      return initialData.filter(service => service.app_name === appType.replace('-', '_'));
    }
  };

  const filteredServices = getFilteredServices();

  // Get page configuration based on app type
  const getPageConfig = () => {
    if (appType === 'google') {
      return {
        title: "Google Apps Configuration",
        description: "Configure your Google services integration. These external apps require API keys and IDs to function properly.",
        sectionTitle: "Google Services",
        showImportantNote: true
      };
    } else if (appType === 'dealer-locator') {
      return {
        title: "Dealer Locator Configuration",
        description: "Configure your dealer locator service. This internal app is system-managed and helps customers find nearby dealers.",
        sectionTitle: "Internal App",
        showImportantNote: false
      };
    } else if (appType === 'checkout-question') {
      return {
        title: "Checkout Questions Configuration",
        description: "Configure checkout questions that customers will see during the checkout process. Add custom questions to collect additional information.",
        sectionTitle: "Checkout Questions",
        showImportantNote: false
      };
    } else {
      // Generic config for other apps
      const appName = appType.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');

      return {
        title: `${appName} Configuration`,
        description: `Configure your ${appName.toLowerCase()} service.`,
        sectionTitle: "Configuration",
        showImportantNote: false
      };
    }
  };

  const config = getPageConfig();

  const initialForm: ThirdPartyServicesFormData = {
    services: filteredServices,
  };

  const handleServiceChange = (
    services: ProcessedService[],
    setData: (data: ThirdPartyServicesFormData) => void
  ) => (appName: string, field: keyof ProcessedService, value: any) => {
    const updatedServices = services.map(service =>
      service.app_name === appName
        ? { ...service, [field]: value }
        : service
    );

    setData({ services: updatedServices });
  };

  return (
    <Form
      initial={initialForm}
      onSubmit={onSubmit}
      confirmLeave
      disabled={disabled}
    >
      {({ data, set, isSaveDisabled, submit }) => {
        const handleChange = handleServiceChange(data.services, set);

        return (
          <DetailPageLayout gridTemplateColumns={1}>
            <TopNav
              href={ExtensionsPaths.installedExtensions}
              title={config.title}
            />
            <DetailPageLayout.Content>
              <Box
                paddingX={8}
                paddingY={6}
                paddingTop={9}
              >
                <Box 
                  as="h1" 
                  style={{
                    fontSize: "24px",
                    fontWeight: 500,
                    color: "#202124",
                    marginBottom: "8px"
                  }}
                >
                  {config.title}
                </Box>
                <Box 
                  as="div" 
                  style={{
                    fontSize: "14px",
                    color: "#5f6368",
                    marginBottom: "24px",
                    lineHeight: "1.5"
                  }}
                >
                  {config.description}
                </Box>
                
                {errors.length > 0 && (
                  <Box
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginBottom: "16px"
                    }}
                  >
                    {errors.map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </Box>
                )}

                {config.showImportantNote && (
                  <Box
                    style={{
                      backgroundColor: "#1a73e8",
                      color: "white",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginBottom: "24px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}
                  >
                    <Box 
                      style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "white",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1a73e8",
                        fontWeight: "bold",
                        fontSize: "14px",
                        flexShrink: 0
                      }}
                    >
                      !
                    </Box>
                    <Box 
                      as="div" 
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.4"
                      }}
                    >
                      <strong>Important:</strong> These Google API keys and IDs must be configured first. Changes will take effect immediately after saving.
                    </Box>
                  </Box>
                )}

                <Box 
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "24px",
                    marginBottom: "16px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
                  }}
                >
                  <Box 
                    as="h2" 
                    style={{
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#202124",
                      marginBottom: "20px"
                    }}
                  >
                    {config.sectionTitle}
                  </Box>
                  <Box>
                    {data.services.map((service, index, array) => (
                      <Box key={service.app_name} style={{ borderBottom: index === array.length - 1 ? "none" : "1px solid #e8eaed" }}>
                        <ServiceCard
                          service={service}
                          onChange={handleChange}
                          disabled={disabled}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DetailPageLayout.Content>
              <Savebar>
                <Savebar.Spacer />
                <Savebar.CancelButton onClick={() => navigate(ExtensionsPaths.installedExtensions)} />
                <Savebar.ConfirmButton
                  transitionState={saveButtonBarState}
                  onClick={submit}
                  disabled={!!isSaveDisabled}
                />
              </Savebar>
          </DetailPageLayout>
        );
      }}
    </Form>
  );
};